import React, { useEffect, useRef, useState } from 'react';
import { api, fileUrl } from '@/lib/api';
import { TextField, Toggle, Section } from '@/admin/AdminUI';
import ImageUploader from '@/admin/ImageUploader';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

// Upcoming Events admin — same CRUD shape as NewsTab. The homepage
// section sits between Hero and Experiences and shows every VISIBLE
// event in this list's order; with zero visible events the section
// (heading and dividers included) disappears entirely. There is no
// section-wide switch — visibility lives on each event.

// Same rule as the backend validator: really parse it, and require an
// http(s) scheme AND a nonempty hostname — "https://?" is not a link.
const isValidRegistrationUrl = (u) => {
  try {
    const p = new URL(u);
    return (p.protocol === 'http:' || p.protocol === 'https:') && p.hostname.length > 0;
  } catch (e) {
    return false;
  }
};

export default function UpcomingEventsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  // busy blocks every row mutation (move/delete/toggle) while one is in
  // flight, so rapid clicks can never double-submit.
  const [busy, setBusy] = useState(false);
  // One visible error line for LIST-LEVEL paths (reorder/delete/toggle).
  // A failed load must never masquerade as "No events yet" — loadFailed
  // keeps the empty state and the failure state distinct, with a Retry.
  const [error, setError] = useState('');
  // Editor-dialog errors are a SEPARATE state, rendered inside the open
  // dialog with role="alert" — a save failure must appear where Sol is
  // looking, never behind the modal. Cleared on open and on success.
  const [editorError, setEditorError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // Editor dialog accessibility: the element that opened the dialog
  // gets focus back on close, Escape closes, and focus moves into the
  // dialog when it opens.
  const dialogRef = useRef(null);
  const openerRef = useRef(null);
  // Synchronous mirror of `saving`, set BEFORE the request starts. The
  // Escape listener, backdrop click, closeEditor and every field guard
  // read THIS, never a `saving` value captured at dialog-open render —
  // React's later re-render is too late to stop a same-tick event.
  const savingRef = useRef(false);

  // List request ownership: only the NEWEST request generation may
  // write items/loaded/failure state; stale successes, stale failures,
  // and responses landing after unmount are all discarded.
  const listReqGenRef = useRef(0);
  const loadingRef = useRef(false); // single-flight guard for load/Retry
  const aliveRef = useRef(true);

  // load() never clears `error` itself: a mutation failure sets the
  // message and a following successful refresh must not wipe it before
  // the admin reads it. Each mutation's SUCCESS path clears the error.
  // Fail-closed: readiness (`loaded`) is revoked while the newest
  // request is outstanding, so Add and every row mutation stay
  // disabled until a REAL array has come back. A 200 whose body is not
  // an array is a LOAD FAILURE, never an empty list.
  const load = async () => {
    if (loadingRef.current) return; // single-flight: one request at a time
    loadingRef.current = true;
    const gen = listReqGenRef.current + 1;
    listReqGenRef.current = gen;
    setLoadFailed(false);
    setLoaded(false);
    try {
      const r = await api.get('/admin/upcoming-events');
      if (!aliveRef.current || gen !== listReqGenRef.current) return; // stale/unmounted — discard
      if (!Array.isArray(r.data)) {
        setLoadFailed(true); // malformed 200 = failure, mutations stay locked
        return;
      }
      setItems(r.data);
      setLoaded(true);
    } catch (e) {
      if (!aliveRef.current || gen !== listReqGenRef.current) return; // stale/unmounted — discard
      setLoadFailed(true);
    } finally {
      // Only the request that still OWNS the current generation may
      // release the single-flight lock — a superseded request (e.g.
      // the pre-StrictMode-remount load) must not unlock a newer one
      // that is still in flight.
      if (gen === listReqGenRef.current) loadingRef.current = false;
    }
  };
  // Component lifetime + initial load in ONE effect. Every SETUP marks
  // the component alive again — React StrictMode mounts, cleans up,
  // and remounts, and a cleanup-only aliveRef would stay false forever,
  // discarding the replacement load's response and leaving the tab
  // stuck on "Loading events…". Cleanup invalidates this mount's
  // request generation and releases the single-flight lock so the
  // remount's replacement request starts immediately instead of being
  // blocked by the doomed first request.
  useEffect(() => {
    aliveRef.current = true;
    load();
    return () => {
      aliveRef.current = false;
      listReqGenRef.current += 1; // orphan this mount's in-flight request
      loadingRef.current = false; // let the remount start its own load
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every list mutation (and Add) waits until the newest list request
  // has returned a valid array.
  const listReady = loaded && !loadFailed;

  // Escape closes the dialog — but reads savingRef.current AT KEY TIME,
  // never the `saving` value captured when the dialog opened, so a save
  // that started after mount still blocks Escape. Gated on the
  // OPEN/CLOSED boolean, not the editing object, so keystrokes in the
  // fields don't re-run it.
  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !savingRef.current) closeEditor();
    };
    document.addEventListener('keydown', onKey);
    // Initial focus: the dialog container itself (tabIndex -1), so the
    // next Tab lands on the first field.
    const raf = requestAnimationFrame(() => {
      if (dialogRef.current) dialogRef.current.focus();
    });
    return () => {
      document.removeEventListener('keydown', onKey);
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!editing]);

  const openEditor = (item) => {
    openerRef.current = document.activeElement;
    setEditorError('');
    setEditing(item);
  };
  const closeEditor = () => {
    if (savingRef.current) return; // a saving editor can never be closed
    setEditing(null);
    setEditorError('');
    const opener = openerRef.current;
    openerRef.current = null;
    if (opener && typeof opener.focus === 'function') opener.focus();
  };
  // Every editor field funnels through this guard: once Save begins,
  // no keystroke, toggle, or image change can alter the in-flight
  // payload or the on-screen values (synchronous ref — not a render).
  const editField = (patch) => {
    if (savingRef.current) return;
    setEditing((cur) => (cur ? { ...cur, ...patch } : cur));
  };

  const addNew = () => openEditor({
    name: '', date: '', location: '', description: '', image: '', registration_url: '', status_label: '', visible: true,
  });
  const startEdit = (it) => openEditor({ ...it });

  // display_order is deliberately absent from every payload: the server
  // assigns it on create and ignores ordinary edits. Only the arrow
  // buttons (the reorder route) change order — never a typed number.
  const payloadFrom = (src, overrides = {}) => ({
    name: (src.name || '').trim(),
    date: (src.date || '').trim(),
    location: (src.location || '').trim(),
    description: (src.description || '').trim(),
    image: (src.image || '').trim(),
    registration_url: (src.registration_url || '').trim(),
    status_label: (src.status_label || '').trim(),
    visible: src.visible !== false,
    ...overrides,
  });

  // Mutations reload ONLY after success, so a failure message stays on
  // screen instead of being wiped by the refresh that follows it.
  const remove = async (id) => {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    setBusy(true);
    try {
      await api.delete(`/admin/upcoming-events/${id}`);
      setError('');
      await load();
    } catch (e) {
      setError('Delete failed. The event is unchanged — try again.');
    } finally { setBusy(false); }
  };

  const move = async (id, dir) => {
    const idx = items.findIndex((i) => i.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const prev = items;
    const next = [...items];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    setBusy(true);
    try {
      await api.put('/admin/upcoming-events/reorder', { items: next.map((it, i) => ({ id: it.id, order: i + 1 })) });
      setError('');
      await load();
    } catch (e) {
      // Never let the admin keep looking at a GUESSED order after a
      // failed reorder — re-read the server: its response IS the saved
      // order. Set the error after the reload so it stays visible.
      try {
        const r = await api.get('/admin/upcoming-events');
        if (!aliveRef.current) return;
        if (!Array.isArray(r.data)) {
          // Malformed recovery body proves NOTHING about the saved
          // order — treat it exactly like an unreachable server.
          throw new Error('malformed recovery response');
        }
        listReqGenRef.current += 1; // stale list requests may no longer write
        setItems(r.data);
        setLoaded(true);
        setLoadFailed(false);
        setError('Reorder failed. The list has been refreshed to the saved order — try again.');
      } catch (e2) {
        if (!aliveRef.current) return;
        // Could not verify what the server holds. Show the pre-move
        // copy as an UNVERIFIED reference only — and lock every
        // mutation (loaded=false kills listReady) until a Retry
        // brings back a real array. The loadFailed banner provides
        // that Retry.
        setItems(prev);
        setLoaded(false);
        setLoadFailed(true);
        setError('Reorder failed and the saved order could not be verified. The list below is the unverified pre-move copy — use Retry to reload before making more changes.');
      }
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (savingRef.current) return; // one save at a time
    const p = payloadFrom(editing);
    // Validation failures render INSIDE the dialog (role="alert"),
    // where Sol is looking — never in the list behind the modal.
    if (!p.name) { setEditorError('An event name is required.'); return; }
    if (!p.date) { setEditorError('A date is required (any display text, e.g. "July 7-9, 2026").'); return; }
    if (!p.location) { setEditorError('A location is required.'); return; }
    if (!p.description) { setEditorError('A description is required.'); return; }
    if (!p.image) { setEditorError('An event image is required.'); return; }
    if (!p.status_label) { setEditorError('A status label is required (e.g. "Register Now" or "Coming Soon").'); return; }
    if (p.registration_url && !isValidRegistrationUrl(p.registration_url)) {
      setEditorError('The registration link must be a complete address starting with http:// or https:// — or leave it empty to show the status as plain text.');
      return;
    }
    setEditorError('');
    savingRef.current = true; // synchronous lock BEFORE the request
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/upcoming-events/${editing.id}`, p);
      } else {
        await api.post('/admin/upcoming-events', p);
      }
      // Success: release the lock FIRST (closeEditor refuses to close a
      // saving editor), then close normally and refresh the list.
      savingRef.current = false;
      setSaving(false);
      closeEditor();
      setError('');
      await load();
    } catch (e) {
      // Failure: the dialog stays open with every entered value intact,
      // and the error appears inside it.
      setEditorError(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Save failed. Nothing was changed — try again.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const toggleVisible = async (it) => {
    setBusy(true);
    try {
      // Atomic visibility-only endpoint: sends nothing but the new
      // boolean, so a stale row copy can never overwrite another
      // admin's newer text/image edits on the same event.
      await api.put(`/admin/upcoming-events/${it.id}/visibility`, { visible: !(it.visible !== false) });
      setError('');
      await load();
    } catch (e) {
      setError('Could not change visibility. The event is unchanged — try again.');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <Section
        title="Upcoming Events"
        action={<button onClick={addNew} disabled={!listReady} className="btn-primary !py-2 !px-4 text-[12px] disabled:opacity-50" data-testid="upcoming-events-add"><Plus size={14} className="mr-1" /> Add Event</button>}
      >
        <p className="font-body text-[12px] text-ink-muted -mt-2">
          Shown on the homepage between the Hero and Experiences sections as flip cards (image, name and date on the front; description, location and the status or registration button on the back). Use the arrows to set the order — events never expire on their own. When every event is hidden, the whole section disappears from the homepage.
        </p>
        {error && (
          <p className="font-body text-[12px] font-semibold text-brand-red border border-brand-red/40 bg-brand-red/5 rounded-sm px-3 py-2" data-testid="upcoming-events-admin-error">
            {error}
          </p>
        )}
        {loadFailed && (
          <p className="font-body text-[12px] font-semibold text-brand-red border border-brand-red/40 bg-brand-red/5 rounded-sm px-3 py-2" data-testid="upcoming-events-load-error">
            Could not load the events list.
            <button onClick={load} className="btn-secondary !py-1 !px-3 text-[11px] ml-3" data-testid="upcoming-events-retry">Retry</button>
          </p>
        )}
        <div className="space-y-2.5">
          {items.map((it, idx) => (
            <div
              key={it.id}
              className="flex items-center gap-3 p-3 border border-border-line rounded-sm bg-white"
              data-testid="upcoming-event-row-admin"
            >
              <span className="font-display font-bold text-[12px] text-ink-soft w-6 text-center">{it.display_order}</span>
              <div className="w-16 h-11 border border-border-line rounded-sm bg-bg-canvas overflow-hidden shrink-0 flex items-center justify-center">
                {it.image ? (
                  <img src={fileUrl(it.image) || it.image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-body text-[9px] uppercase text-ink-soft">No image</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold uppercase text-ink-primary text-[13px] tracking-tightish truncate">{it.name}</div>
                <div className="font-body text-[11px] text-ink-muted line-clamp-1">{it.date} — {it.location}</div>
              </div>
              <span className="font-body text-[11px] uppercase tracking-wider2 text-ink-soft">
                {it.visible !== false ? 'Visible' : 'Hidden'}
              </span>
              <div className="flex flex-col gap-1">
                <button onClick={() => move(it.id, -1)} disabled={!listReady || busy || idx === 0} className="p-1 disabled:opacity-30" aria-label={`Move ${it.name} up`}><ChevronUp size={14} /></button>
                <button onClick={() => move(it.id, 1)} disabled={!listReady || busy || idx === items.length - 1} className="p-1 disabled:opacity-30" aria-label={`Move ${it.name} down`}><ChevronDown size={14} /></button>
              </div>
              <button onClick={() => toggleVisible(it)} disabled={!listReady || busy} className="btn-secondary !py-2 !px-3 text-[11px] disabled:opacity-50" data-testid="upcoming-event-toggle-visible">
                {it.visible !== false ? 'Hide' : 'Show'}
              </button>
              <button onClick={() => startEdit(it)} disabled={!listReady || busy} className="btn-secondary !py-2 !px-3 text-[11px] disabled:opacity-50">Edit</button>
              <button onClick={() => remove(it.id)} disabled={!listReady || busy} className="btn-secondary !py-2 !px-3 text-[11px] disabled:opacity-50" aria-label={`Delete ${it.name}`}><Trash2 size={14} /></button>
            </div>
          ))}
          {loaded && !loadFailed && items.length === 0 && (
            <p className="font-body text-[12px] text-ink-muted py-4">No events yet. The homepage section stays hidden until the first visible event is added.</p>
          )}
          {!loaded && !loadFailed && (
            <p className="font-body text-[12px] text-ink-muted py-4">Loading events…</p>
          )}
        </div>
      </Section>

      {editing && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
          onClick={() => { if (!savingRef.current) closeEditor(); }}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="upcoming-event-editor-title"
            tabIndex={-1}
            className="bg-bg-elevated max-w-2xl w-full rounded-lg p-6 max-h-[90vh] overflow-y-auto focus:outline-none"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="upcoming-event-editor-title" className="font-display font-bold uppercase text-[18px] mb-4">
              {editing.id ? 'Edit Event' : 'New Event'}
            </h3>
            {/* Editor-local errors live INSIDE the dialog, announced to
                assistive technology. List errors never render here. */}
            {editorError && (
              <p role="alert" className="mb-4 font-body text-[12px] font-semibold text-brand-red border border-brand-red/40 bg-brand-red/5 rounded-sm px-3 py-2" data-testid="upcoming-event-editor-error">
                {editorError}
              </p>
            )}
            {/* The disabled fieldset freezes every native field the
                moment Save begins; the editField guard (synchronous
                savingRef check) backstops anything non-native inside
                ImageUploader. */}
            <fieldset disabled={saving} className="space-y-4 border-0 p-0 m-0 min-w-0">
              <TextField
                label="Event name"
                value={editing.name}
                onChange={(v) => editField({ name: v })}
                testid="upcoming-event-edit-name"
              />
              <TextField
                label='Date (display text — a date or a range, e.g. "July 7-9, 2026")'
                value={editing.date}
                onChange={(v) => editField({ date: v })}
                testid="upcoming-event-edit-date"
              />
              <TextField
                label="Location"
                value={editing.location}
                onChange={(v) => editField({ location: v })}
                testid="upcoming-event-edit-location"
              />
              <div>
                <label className="block">
                  <span className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-1.5">Description (shows on the back of the card)</span>
                  <textarea
                    value={editing.description || ''}
                    onChange={(e) => editField({ description: e.target.value })}
                    rows={4}
                    className="w-full border border-border-line rounded-sm px-3 py-2 font-body text-[14px] bg-white focus:outline-none focus:border-ink-primary"
                    data-testid="upcoming-event-edit-description"
                  />
                </label>
              </div>
              <ImageUploader
                value={editing.image}
                onChange={(v) => editField({ image: v })}
                label="Event image (front of the card)"
                testid="upcoming-event-edit-image"
              />
              <TextField
                label='Status label (e.g. "Register Now" or "Coming Soon")'
                value={editing.status_label}
                onChange={(v) => editField({ status_label: v })}
                testid="upcoming-event-edit-status"
              />
              <TextField
                label="Registration link (optional — with a link the status label becomes the button; empty shows it as plain text)"
                value={editing.registration_url}
                onChange={(v) => editField({ registration_url: v })}
                placeholder="https://…"
                testid="upcoming-event-edit-url"
              />
              {/* No Order field: the server assigns placement on create
                  and the arrows in the list are the only way to change it. */}
              <Toggle
                label="Visible on site"
                value={editing.visible !== false}
                onChange={(v) => editField({ visible: v })}
                testid="upcoming-event-edit-visible"
              />
            </fieldset>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={closeEditor} disabled={saving} className="btn-secondary text-[12px] !py-2 !px-4 disabled:opacity-50">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary text-[12px] !py-2 !px-4 disabled:opacity-50" data-testid="upcoming-event-edit-save">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
